import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGlobal } from 'reactn';
import store, { showToast, showToastWithType } from '../../../../core/store';
import { createCasePlanWidget, exportCasePlan, fetchCasePlanDetail, fetchCasePlanWidgetTypes, patchCasePlan, patchCasePlanWidget } from '../../api/casePlanDetailApi';
import { fetchLearnerReentryPlan } from '../../api/learnerReentryPlanApi';
import {
  CASE_PLAN_ADD_EDITOR_PERMISSIONS,
  CASE_PLAN_EXPORT_SUCCESS_MESSAGE,
  canTransitionStatus,
  CHECKLIST_WIDGET_TYPE,
  NOTE_WIDGET_TYPE,
  PLAN_STATUS,
  PLAN_STATUS_CONFIRM_COPY,
  PLAN_STATUS_UPDATE_FAILED_MESSAGE,
  PLAN_STATUS_UPDATE_FORBIDDEN_MESSAGE,
  PLAN_STATUS_UPDATE_INVALID_MESSAGE,
  PLAN_STATUS_UPDATE_SUCCESS_MESSAGE,
  REENTRY_PLANS_LIST_PATH,
  WIDGET_CATALOG_LOAD_FAILED_MESSAGE,
  WIDGET_CREATE_FAILED_MESSAGE,
  WIDGET_DESCRIPTION_UPDATE_FAILED_MESSAGE,
} from '../../constants/reentry-planning.constants';
import { canManagePlanEditors } from '../../utils/case-plan-editors.util';
import { isAnyPermitted } from '../../utils/case-plan-permission.util';
import {
  appendWidgetToSection,
  buildPendingWidget,
  buildPendingWidgetClientId,
  buildReentryPlanItems,
  fetchReentryPlanIfNeeded,
  findMappedWidgetById,
  loadCasePlanDetailData,
  mapWidget,
  removeWidgetFromSection,
  replaceWidgetInSection,
  updateWidgetDescription,
} from '../../utils/reentry-plan-detail.util';
import { useChecklistWidgetMutations } from '../../widgets/checklist';
import { useNoteWidgetMutations } from '../../widgets/note';
import { useCasePlanSection } from './useCasePlanSection';
import { useCasePlanWidget } from './useCasePlanWidget';

const CONFIRMABLE_STATUSES = new Set(Object.keys(PLAN_STATUS_CONFIRM_COPY));

const getErrorStatus = (error) => error?.status ?? error?.statusCode;

export const useCasePlanDetailsHook = (casePlanId) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [global] = useGlobal();
  const { cbacConfigFlag, permissions, permissionIds } = global ?? {};
  const [cardProps, setCardProps] = useState(null);
  const [plan, setPlan] = useState(null);
  const [sections, setSections] = useState([]);
  const [isSectionsLoading, setIsSectionsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isEditorsOpen, setIsEditorsOpen] = useState(false);
  const [isAddWidgetOpen, setIsAddWidgetOpen] = useState(false);
  const [addWidgetSectionId, setAddWidgetSectionId] = useState(null);
  const [widgetTypeCategories, setWidgetTypeCategories] = useState([]);
  const [planStatus, setPlanStatus] = useState(null);
  const [isEditor, setIsEditor] = useState(() => location.state?.isEditor === true);
  const [planId, setPlanId] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Optimistic handlers read the latest sections without re-creating their callbacks.
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  const handleBack = useCallback(() => {
    navigate(REENTRY_PLANS_LIST_PATH);
  }, [navigate]);

  // Keyed by the stable section key, not the section id, so collapse state survives a
  // pending section's temporary id being replaced by its server UUID.
  const handleToggleCollapse = useCallback((sectionKey) => {
    setCollapsedSections((previous) => ({
      ...previous,
      [sectionKey]: !previous[sectionKey],
    }));
  }, []);

  const handleAddWidgetClose = useCallback(() => {
    setIsAddWidgetOpen(false);
    setAddWidgetSectionId(null);
    setWidgetTypeCategories([]);
  }, []);

  const handleAddWidget = useCallback(async (sectionId) => {
    setAddWidgetSectionId(sectionId);
    setWidgetTypeCategories([]);

    try {
      const response = await fetchCasePlanWidgetTypes();
      const categories = Array.isArray(response?.categories) ? response.categories : [];
      setWidgetTypeCategories(categories);
      setIsAddWidgetOpen(true);
    } catch {
      setAddWidgetSectionId(null);
      store.dispatch(showToast(WIDGET_CATALOG_LOAD_FAILED_MESSAGE));
    }
  }, []);

  const resolveCreatedWidgetItems = useCallback(
    async (createdWidget) => {
      const reentryPlan = await fetchReentryPlanIfNeeded(
        [{ widgets: [createdWidget] }],
        plan?.personId,
        fetchLearnerReentryPlan,
      );

      return buildReentryPlanItems(reentryPlan);
    },
    [plan?.personId],
  );

  const persistCreatedWidget = useCallback(
    async (sectionId, clientWidgetId, widgetTypeKey) => {
      try {
        const created = await createCasePlanWidget(casePlanId, {
          sectionId,
          widgetType: widgetTypeKey,
          config: {},
        });

        if (typeof created?.id !== 'string' || typeof created?.widgetType !== 'string') {
          throw created;
        }

        const reentryPlanItems = await resolveCreatedWidgetItems(created);
        const mapped = mapWidget(created, reentryPlanItems);

        setSections((previous) =>
          replaceWidgetInSection(previous, sectionId, clientWidgetId, mapped),
        );
      } catch {
        setSections((previous) =>
          removeWidgetFromSection(previous, sectionId, clientWidgetId),
        );
        store.dispatch(showToast(WIDGET_CREATE_FAILED_MESSAGE));
      }
    },
    [casePlanId, resolveCreatedWidgetItems],
  );

  const handleAddWidgetSelect = useCallback(
    (widgetType) => {
      if (!casePlanId || !addWidgetSectionId || !widgetType?.key) {
        return;
      }

      const sectionId = addWidgetSectionId;
      const clientWidgetId = buildPendingWidgetClientId();
      const optimisticWidget = buildPendingWidget(widgetType, clientWidgetId);

      setSections((previous) => appendWidgetToSection(previous, sectionId, optimisticWidget));
      handleAddWidgetClose();
      void persistCreatedWidget(sectionId, clientWidgetId, widgetType.key);
    },
    [casePlanId, addWidgetSectionId, handleAddWidgetClose, persistCreatedWidget],
  );

  const handleWidgetDescriptionBlur = useCallback(
    async (widgetId, description) => {
      if (!casePlanId || !widgetId) {
        return;
      }

      const previousDescription =
        findMappedWidgetById(sectionsRef.current, widgetId)?.description ?? '';

      if (previousDescription === description) {
        return;
      }

      // Paint the new description immediately; roll back if the PATCH fails.
      setSections((previous) => updateWidgetDescription(previous, widgetId, description));

      try {
        await patchCasePlanWidget(casePlanId, widgetId, { description });
      } catch {
        setSections((previous) =>
          updateWidgetDescription(previous, widgetId, previousDescription),
        );
        store.dispatch(showToast(WIDGET_DESCRIPTION_UPDATE_FAILED_MESSAGE));
      }
    },
    [casePlanId],
  );

  const handleEditorsClick = useCallback(() => {
    setIsEditorsOpen(true);
  }, []);

  const handleEditorsClose = useCallback(() => {
    setIsEditorsOpen(false);
  }, []);

  const handleExportClick = useCallback(() => {
    setIsExportOpen(true);
  }, []);

  const handleExportClose = useCallback(() => {
    setIsExportOpen(false);
  }, []);

  const handleExportSubmit = useCallback(async ({ sectionIds, emails, password }) => {
    try {
      await exportCasePlan(casePlanId, { sectionIds, emails, password });
      store.dispatch(showToastWithType(CASE_PLAN_EXPORT_SUCCESS_MESSAGE, 'success'));
    } catch {
      store.dispatch(showToast('Failed to export case plan. Please try again.'));
      throw new Error('Case plan export failed');
    }
  }, [casePlanId]);

  const handleStatusSelect = useCallback((value) => {
    if (value === planStatus || !canTransitionStatus(planStatus, value)) {
      return;
    }

    if (!CONFIRMABLE_STATUSES.has(value) || value === PLAN_STATUS.DRAFT) {
      return;
    }

    setPendingStatus(value);
  }, [planStatus]);

  const handleStatusCancel = useCallback(() => {
    setPendingStatus(null);
  }, []);

  /** Re-runs the detail load; used when a section mutation reveals stale local state. */
  const reloadDetail = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const resetLoadingState = useCallback(() => {
    setIsSectionsLoading(true);
    setError(null);
    setCardProps(null);
    setPlan(null);
    setSections([]);
    setPlanStatus(null);
    setIsEditor(location.state?.isEditor === true);
    setPlanId(null);
    setPendingStatus(null);
    setIsEditorsOpen(false);
    setIsAddWidgetOpen(false);
    setAddWidgetSectionId(null);
    setWidgetTypeCategories([]);
  }, [location.state]);

  const applyLoadedDetail = useCallback((detail) => {
    setCardProps(detail.cardProps);
    setPlan(detail.plan ?? null);
    setSections(detail.sections);
    const editor = detail.isEditor === true || detail.plan?.isEditor === true;
    setPlanStatus(detail.status ?? detail.plan?.status ?? null);
    setIsEditor(editor);
    setPlanId(detail.planId ?? detail.plan?.id ?? null);
  }, []);

  const applyLoadFailure = useCallback(() => {
    setCardProps(null);
    setSections([]);
    setError('Failed to load case plan details');
    setPlanStatus(null);
    setIsEditor(false);
    setPlanId(null);
    setPendingStatus(null);
  }, []);

  const finishLoading = useCallback(() => {
    setIsSectionsLoading(false);
  }, []);

  const applyMissingCasePlan = useCallback(() => {
    setError('Case plan not found');
    setIsSectionsLoading(false);
  }, []);

  const loadCasePlanDetail = useCallback(
    async (getIsCancelled, signal) => {
      resetLoadingState();

      try {
        const detail = await loadCasePlanDetailData(casePlanId, {
          fetchCasePlanDetail,
          fetchLearnerReentryPlan,
          signal,
        });

        if (getIsCancelled()) {
          return;
        }

        applyLoadedDetail(detail);
      } catch (loadError) {
        if (getIsCancelled() || loadError?.name === 'AbortError') {
          return;
        }

        applyLoadFailure();
      } finally {
        if (!getIsCancelled()) {
          finishLoading();
        }
      }
    },
    [casePlanId, resetLoadingState, applyLoadedDetail, applyLoadFailure, finishLoading],
  );

  const handleStatusConfirm = useCallback(async () => {
    const target = pendingStatus;
    if (!target || !casePlanId) {
      return;
    }

    try {
      const body = await patchCasePlan(casePlanId, { status: target });
      const nextStatus = body?.plan?.status ?? target;
      setPlanStatus(nextStatus);
      setPlan((previousPlan) =>
        previousPlan ? { ...previousPlan, status: nextStatus } : previousPlan,
      );
      setPendingStatus(null);
      store.dispatch(showToastWithType(PLAN_STATUS_UPDATE_SUCCESS_MESSAGE, 'success'));
    } catch (patchError) {
      setPendingStatus(null);
      const statusCode = getErrorStatus(patchError);
      if (statusCode === 403 || statusCode === 422) {
        store.dispatch(
          showToast(
            statusCode === 403
              ? PLAN_STATUS_UPDATE_FORBIDDEN_MESSAGE
              : PLAN_STATUS_UPDATE_INVALID_MESSAGE,
          ),
        );
        await loadCasePlanDetail(() => false);
        return;
      }

      store.dispatch(showToast(PLAN_STATUS_UPDATE_FAILED_MESSAGE));
    }
  }, [pendingStatus, casePlanId, loadCasePlanDetail]);

  useEffect(() => {
    if (!casePlanId) {
      applyMissingCasePlan();
      return;
    }

    const abortController = new AbortController();
    const getIsCancelled = () => abortController.signal.aborted;

    loadCasePlanDetail(getIsCancelled, abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [casePlanId, loadCasePlanDetail, applyMissingCasePlan, reloadToken]);

  const sectionApi = useCasePlanSection({
    casePlanId,
    plan,
    sections,
    setSections,
    reloadDetail,
  });

  const checklistWidgetHandlers = useChecklistWidgetMutations({
    casePlanId,
    sections,
    setSections,
  });

  const noteWidgetHandlers = useNoteWidgetMutations({
    casePlanId,
    sections,
    setSections,
  });

  const widgetEditHandlers = useMemo(
    () =>
      sectionApi.canManageSections
        ? {
            [CHECKLIST_WIDGET_TYPE]: checklistWidgetHandlers,
            [NOTE_WIDGET_TYPE]: noteWidgetHandlers,
          }
        : undefined,
    [sectionApi.canManageSections, checklistWidgetHandlers, noteWidgetHandlers],
  );

  const sectionApiWithWidgetHandlers = useMemo(
    () => ({
      ...sectionApi,
      // Only EditableCasePlanSection consumes these; read-only sections keep mapped readOnly.
      widgetEditHandlers,
    }),
    [sectionApi, widgetEditHandlers],
  );
  const hasAddEditorPermission = isAnyPermitted(
    cbacConfigFlag,
    permissions,
    CASE_PLAN_ADD_EDITOR_PERMISSIONS,
    permissionIds,
  );
  const isPreviewPlan = plan?.isWritablePlanType === false;
  const canManageEditors = canManagePlanEditors({
    isEditor,
    hasAddEditorPermission,
    isPreviewPlan,
  });

  const handleEditorsSaved = useCallback(() => {
    setIsEditorsOpen(false);
    reloadDetail();
  }, [reloadDetail]);

  const widgetApi = useCasePlanWidget({
    casePlanId,
    plan,
    sections,
    setSections,
    reloadDetail,
    sectionApi,
  });

  return {
    cardProps,
    plan,
    sections,
    isSectionsLoading,
    error,
    collapsedSections,
    handleBack,
    handleToggleCollapse,
    handleAddWidget,
    handleAddWidgetClose,
    handleAddWidgetSelect,
    isAddWidgetOpen,
    widgetTypeCategories,
    handleWidgetDescriptionBlur,
    handleEditorsClick,
    handleEditorsClose,
    handleEditorsSaved,
    isEditorsOpen,
    canManageEditors,
    handleExportClick,
    isExportOpen,
    handleExportClose,
    handleExportSubmit,
    // Archived locks section writes (`canManageSections`), not the status chip / editors.
    isViewOnly: isEditor !== true || plan?.isWritablePlanType === false,
    planStatus,
    isEditor,
    planId,
    pendingStatus,
    isStatusModalOpen: Boolean(pendingStatus),
    handleStatusSelect,
    handleStatusConfirm,
    handleStatusCancel,
    sectionApi: sectionApiWithWidgetHandlers,
    widgetApi,
  };
};
